import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type {
  SignalMetadataFieldDistribution,
  SignalMetadataFieldDistributionEntry,
} from "@repo/app-types/entities/signal";
import { StackedBar } from "@repo/ui/Charts/stackedBar";
import { IconChevronRight } from "@tabler/icons-react";
import { useState, type FC } from "react";
import {
  Button as AriaButton,
  Tree,
  TreeItem,
  TreeItemContent,
  type Key,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { BaseCard } from "../../performanceReports/baseCard.js";
import {
  computeAndFormatPercent,
  computePercent,
  formatDistributionEntryLabel,
  formatMetadataFieldKey,
} from "../signalStatsFormatters.js";
import {
  computeGreenRate,
  resolveSegments,
  toBarSegments,
} from "./signalOutcomeStats.js";

/**
 * Shared grid template for header and body so field / outcome / volume /
 * matches columns line up across the header row and the Tree.
 */
const GRID_COLS = "grid-cols-[minmax(0,1fr)_13rem_13rem_4rem]";

/**
 * Per-value match count = the number of matches with a graded outcome in the
 * shown definition, i.e. the sum of the outcomes that resolve against it.
 * Returns 0 without an outcome definition (nothing resolves).
 */
function matchCount(
  entry: SignalMetadataFieldDistributionEntry,
  outcomeDef: OutcomeDefinition | undefined,
): number {
  if (!outcomeDef) {
    return 0;
  }
  return resolveSegments(entry.outcomes, outcomeDef).reduce(
    (sum, s) => sum + s.count,
    0,
  );
}

export const SignalMetadataBreakdown: FC<{
  metadataBreakdown: readonly SignalMetadataFieldDistribution[];
  outcomeDef: OutcomeDefinition | undefined;
}> = ({ metadataBreakdown, outcomeDef }) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(
    () => new Set(metadataBreakdown.map((field) => field.fieldId)),
  );

  if (metadataBreakdown.length === 0) {
    return (
      <BaseCard title="Match breakdown">
        <p className="text-sm text-muted-foreground">
          No metadata fields configured for this signal.
        </p>
      </BaseCard>
    );
  }

  return (
    <BaseCard title="Match breakdown">
      <div
        className={twMerge(
          "grid items-center gap-x-6 border-b border-border-light pt-1.5 pb-3 text-xs font-medium text-gray-400",
          GRID_COLS,
        )}
      >
        <div className="text-left">Field</div>
        <div className="text-left">Outcome</div>
        <div className="text-left">Volume</div>
        <div className="text-right whitespace-nowrap">Matches</div>
      </div>

      <Tree
        aria-label="Match breakdown"
        className={twMerge("grid grid-flow-row gap-x-6 text-sm", GRID_COLS)}
        expandedKeys={expandedKeys}
        onExpandedChange={setExpandedKeys}
      >
        {metadataBreakdown.map((field) => {
          const total = field.distribution.reduce(
            (sum, e) => sum + matchCount(e, outcomeDef),
            0,
          );
          return (
            <TreeItem
              key={field.fieldId}
              id={field.fieldId}
              textValue={formatMetadataFieldKey(field.fieldKey)}
              className="group col-span-full grid cursor-pointer grid-cols-subgrid border-b border-border-light outline-hidden data-[hovered]:bg-gray-50"
            >
              <TreeItemContent>
                <FieldKeyRow fieldKey={field.fieldKey} />
              </TreeItemContent>
              {field.distribution.map((entry, idx) => (
                <TreeItem
                  key={`${field.fieldId}::${idx}`}
                  id={`${field.fieldId}::${idx}`}
                  textValue={formatDistributionEntryLabel(entry)}
                  className="group col-span-full grid grid-cols-subgrid border-b border-border-light outline-hidden"
                >
                  <TreeItemContent>
                    <FieldValueRow
                      entry={entry}
                      total={total}
                      outcomeDef={outcomeDef}
                    />
                  </TreeItemContent>
                </TreeItem>
              ))}
            </TreeItem>
          );
        })}
      </Tree>
    </BaseCard>
  );
};

const FieldKeyRow: FC<{
  fieldKey: string;
}> = ({ fieldKey }) => (
  <>
    <div className="flex items-center gap-1 py-2 pl-0">
      <AriaButton
        slot="chevron"
        aria-label="Toggle"
        className="flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-gray-100"
      >
        <IconChevronRight className="size-4 transition-transform group-aria-expanded:rotate-90" />
      </AriaButton>
      <span className="truncate font-medium text-foreground">
        {formatMetadataFieldKey(fieldKey)}
      </span>
    </div>
    <div className="py-2" aria-hidden />
    <div className="py-2" aria-hidden />
    <div className="py-2" aria-hidden />
  </>
);

const FieldValueRow: FC<{
  entry: SignalMetadataFieldDistributionEntry;
  total: number;
  outcomeDef: OutcomeDefinition | undefined;
}> = ({ entry, total, outcomeDef }) => {
  const label = formatDistributionEntryLabel(entry);
  const outcomeSegments = outcomeDef
    ? resolveSegments(entry.outcomes, outcomeDef)
    : [];
  const outcomeTotal = outcomeSegments.reduce((sum, s) => sum + s.count, 0);
  const pct = computePercent(outcomeTotal, total);
  const barSegments = toBarSegments(outcomeSegments, outcomeTotal);
  const greenRate = outcomeDef
    ? computeGreenRate(outcomeSegments, outcomeDef)
    : null;
  return (
    <>
      <div className="flex min-w-0 items-center py-2 pl-7">
        <span className="truncate text-foreground" title={label}>
          {label}
        </span>
      </div>
      <div className="flex items-center gap-3 py-2">
        <span className="w-10 shrink-0 text-right whitespace-nowrap text-foreground tabular-nums">
          {greenRate === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            `${Math.round(greenRate * 100)}%`
          )}
        </span>
        <div className="flex-1">
          {barSegments.length > 0 ? (
            <StackedBar
              segments={barSegments}
              showSegmentLabels={false}
              className="h-2 w-full rounded-full"
            />
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-3 py-2">
        <span className="w-10 shrink-0 text-right whitespace-nowrap text-foreground tabular-nums">
          {computeAndFormatPercent(outcomeTotal, total)}
        </span>
        <div className="flex-1">
          <DistributionBar value={pct} />
        </div>
      </div>
      <div className="py-2 text-right whitespace-nowrap text-foreground tabular-nums">
        {outcomeTotal.toLocaleString()}
      </div>
    </>
  );
};

const DistributionBar: FC<{ value: number }> = ({ value }) => {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div
      role="meter"
      aria-label={`${clamped}% of matches`}
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-2 w-full overflow-hidden rounded-full bg-gray-100"
    >
      <div
        className="h-full rounded-full bg-blue-600"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
};
